import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, TextField, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Divider, Typography, FormHelperText
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { enGB } from "date-fns/locale";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { SERVER_URL } from "../../constants";
import { can } from "../shared utils/auth";

export default function AddBookingGroup({
  open,
  onClose,
  onCreated, 
  campuses = [],
  defaultCampus = "",
}) {
  // group fields
  const [groupName, setGroupName] = useState("");
  const [notes, setNotes] = useState("");

  // booking meta (optional)
  const [metaDefaultCampus, setMetaDefaultCampus] = useState(defaultCampus || "");
  const [metaStart, setMetaStart] = useState(null);
  const [metaEnd, setMetaEnd] = useState(null);
  const [metaNationality, setMetaNationality] = useState("");
  const [metaRate, setMetaRate] = useState(""); // string, we’ll coerce to number on submit

  const [saving, setSaving] = useState(false);
  const [campusError, setCampusError] = useState("");

  const campusNames = useMemo(() => (campuses || []).map(c => c.name), [campuses]);
  const isValidCampus = (name) => campusNames.includes(name);

  // reset on open
  useEffect(() => {
    if (open) {
      setGroupName("");
      setNotes("");

      setMetaDefaultCampus(defaultCampus || "");
      setMetaStart(null);
      setMetaEnd(null);
      setMetaNationality("");
      setMetaRate("");
    }
  }, [open, defaultCampus]);

  const canSave = useMemo(() => groupName.trim().length > 0, [groupName]);

  const fmtDate = (d) => {
    if (!d) return null;
    // d is a Date; ensure YYYY-MM-DD (local) — adjust if you prefer UTC
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    const year = d.getFullYear();
    const month = pad(d.getMonth() + 1);
    const day = pad(d.getDate());
    return `${year}-${month}-${day}`;
  };
  
  const handleSave = async () => {
    if (!canSave) return;

    setSaving(true);
    const token = sessionStorage.getItem("bearer");

    // 1) Create the BOOKING campgroup (minimal payload: name/type/campus/notes)
    const groupPayload = {
      groupName: groupName.trim(),
      groupType: "BOOKING",
      campus: metaDefaultCampus?.trim() || null,
      notes: notes?.trim() || "",
    };

    try {
      const res = await fetch(`${SERVER_URL}api/campgroup`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(groupPayload),
      });
      if (!res.ok) throw new Error(`Create group failed (${res.status})`);

      let createdGroup = null;
      try {
        createdGroup = await res.json();
      } catch {
        createdGroup = null;
      }

      // 2) POST booking-meta (optional) if we have an id
      if (createdGroup?.id) {
        const rateVal =
          metaRate === "" || isNaN(Number(metaRate)) ? 0.0 : Number(metaRate);

        const metaBody = {
          defaultCampus: metaDefaultCampus?.trim() || null,
          defaultStartDate: fmtDate(metaStart),
          defaultEndDate: fmtDate(metaEnd),
          defaultNationality: metaNationality?.trim() || null,
          defaultRate: rateVal,
        };

        // If all optional fields are blank, you can skip the call.
        const anyProvided =
          metaBody.defaultCampus ||
          metaBody.defaultStartDate ||
          metaBody.defaultEndDate ||
          metaBody.defaultNationality ||
          (rateVal && rateVal !== 0);

        if (anyProvided) {
          const metaRes = await fetch(
            `${SERVER_URL}api/campgroup/${createdGroup.id}/booking-meta`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
              body: JSON.stringify(metaBody),
            }
          );
          if (!metaRes.ok) {
            console.warn("Booking meta creation failed", metaRes.status);
          }
        }
      }

      onCreated?.();
      onClose?.();
    } catch (e) {
      console.error("Failed to create BOOKING group", e);
      alert("Failed to create group.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Create Booking Group</DialogTitle>
      <DialogContent sx={{ width: 560, maxWidth: "92vw", pt: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
          <Stack spacing={2}>
            <Typography variant="subtitle2">Group</Typography>
            <TextField
              autoFocus
              label="Group Name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              required
              fullWidth
            />

            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2">Booking settings (optional)</Typography>

            {/* Validated Default Campus */}
            <FormControl fullWidth error={!!campusError}>
              <InputLabel id="meta-campus-label">Default Campus</InputLabel>
              <Select
                labelId="meta-campus-label"
                label="Default Campus"
                value={metaDefaultCampus}
                onChange={(e) => setMetaDefaultCampus(e.target.value)}
                required
              >
                {campusNames.length === 0 ? (
                  <MenuItem value="" disabled>
                    No campuses available
                  </MenuItem>
                ) : (
                  campusNames.map((name) => (
                    <MenuItem key={name} value={name}>{name}</MenuItem>
                  ))
                )}
              </Select>
              {campusError && <FormHelperText>{campusError}</FormHelperText>}
            </FormControl>

            <Stack direction="row" spacing={1}>
              <DatePicker
                label="Default Start Date"
                value={metaStart}
                onChange={(v) => setMetaStart(v)}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="Default End Date"
                value={metaEnd}
                onChange={(v) => setMetaEnd(v)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>

            <TextField
              label="Default Nationality"
              value={metaNationality}
              onChange={(e) => setMetaNationality(e.target.value)}
              fullWidth
            />

            {/* The group rate is set by the back office; the server ignores it otherwise. */}
            {can("finance") && (
              <TextField
                label="Default Rate"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                value={metaRate}
                onChange={(e) => setMetaRate(e.target.value)}
                fullWidth
              />
            )}
          </Stack>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!canSave || saving}
        >
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* Optional: tiny floating trigger button if you'd ever want to use it standalone:
export function AddBookingGroupButton({ onClick }) {
  return (
    <Tooltip title="Create booking group">
      <IconButton color="primary" onClick={onClick}><AddIcon /></IconButton>
    </Tooltip>
  );
}
*/
